// The one HTTP client for the Platform API (CLIENT-ARCHITECTURE §4).
// Screens never call fetch directly. This layer:
//   - sets Substratal-Version on every request (the URL carries no version)
//   - adds the signed-in person's Bearer token, refreshing once on 401
//   - sends an X-Request-Id, and keeps the one the API returns for error details
//   - notices Deprecation/Sunset headers so the shell can show a banner
//   - turns error responses into ApiError
import createClient, { type Middleware } from "openapi-fetch";
import { API_BASE_URL, API_VERSION } from "./config";
import { ApiError, type ApiErrorBody } from "./errors";
import type { paths } from "./schema";
import { session } from "./session";

export interface DeprecationNotice {
  version: string;
  deprecation: string | null;
  sunset: string | null;
}

type Listener = (notice: DeprecationNotice) => void;
const deprecationListeners = new Set<Listener>();
export function onDeprecation(listener: Listener): () => void {
  deprecationListeners.add(listener);
  return () => deprecationListeners.delete(listener);
}

const RETRIED = new WeakSet<Request>();

const middleware: Middleware = {
  async onRequest({ request }) {
    request.headers.set("Substratal-Version", API_VERSION);
    if (!request.headers.has("X-Request-Id")) request.headers.set("X-Request-Id", `req_web_${crypto.randomUUID().replaceAll("-", "")}`);
    const token = session.accessToken();
    if (token && !request.headers.has("Authorization")) request.headers.set("Authorization", `Bearer ${token}`);
    return request;
  },
  async onResponse({ request, response }) {
    const deprecation = response.headers.get("Deprecation");
    const sunset = response.headers.get("Sunset");
    if (deprecation || sunset) {
      const notice = { version: response.headers.get("Substratal-Version") ?? API_VERSION, deprecation, sunset };
      deprecationListeners.forEach((l) => l(notice));
    }
    // One refresh and one replay on an expired access token. Only reads are
    // replayed: a write's body has already been consumed, and the session's
    // proactive refresh (60 s before expiry) keeps writes from reaching this.
    const replayable = request.method === "GET" || request.method === "HEAD";
    if (response.status === 401 && replayable && session.isSignedIn() && !RETRIED.has(request) && !request.url.includes("/auth/")) {
      const refreshed = await session.refresh();
      if (refreshed) {
        const retry = new Request(request, { headers: new Headers(request.headers) });
        retry.headers.set("Authorization", `Bearer ${session.accessToken()}`);
        RETRIED.add(retry);
        return fetch(retry);
      }
    }
    return response;
  },
};

// fetch is looked up per call (not captured at import), so tests and polyfills can replace it.
export const api = createClient<paths>({ baseUrl: API_BASE_URL, fetch: (request) => globalThis.fetch(request) });
api.use(middleware);

/**
 * Unwraps an openapi-fetch result: returns data, or throws ApiError.
 * Use inside TanStack Query functions so errors reach the screen's error state.
 */
export async function call<T>(promise: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  let result: { data?: T; error?: unknown; response: Response };
  try {
    result = await promise;
  } catch {
    throw new ApiError(0, null, null, null);
  }
  const { data, error, response } = result;
  if (response.ok) return data as T;
  const body = (error as { error?: ApiErrorBody } | undefined)?.error ?? null;
  const retryAfter = Number(response.headers.get("Retry-After")) || null;
  throw new ApiError(response.status, body, response.headers.get("X-Request-Id"), retryAfter);
}
